import { useEffect, useMemo, useState } from 'react';

import { useNavStore } from '@/stores/nav';

import { useOfflineFacilities } from './use-offline-facilities';
import { useUserOrgFacilityList } from './use-user-org-facility-list';

const OFFLINE_KEYWORD = 'offline';

/**
 * Returns the filtered list of organizations with their facilities for the current user based on the search query
 * @returns {UserOrganization[]}
 */
export const useFilteredOrgFacilities = () => {
  const { data: orgFacilityList } = useUserOrgFacilityList();
  const [filteredOrgFacilityList, setFilteredOrgFacilityList] = useState(orgFacilityList);
  const { facilitySearchQuery } = useNavStore();

  const { offlineFacilitiesByOrg } = useOfflineFacilities();

  useEffect(() => {
    const searchTerm = facilitySearchQuery.toLowerCase().trim();

    if (!searchTerm) {
      setFilteredOrgFacilityList(orgFacilityList);
      return;
    }

    // If the search term is "offline", filter the list to only include organizations with offline facilities
    if (searchTerm.toLowerCase() === OFFLINE_KEYWORD) {
      const filteredList = orgFacilityList?.reduce(
        (acc, org) => {
          // Get offline facilities for this org
          const offlineFacilities = offlineFacilitiesByOrg[org.organizationId] || [];

          // If org has any offline facilities, include it with only the offline facilities
          if (offlineFacilities.length > 0) {
            acc.push({
              ...org,
              facilities: (org.facilities || []).filter((facility) =>
                offlineFacilities.includes(facility.facilityId)
              ),
            });
          }

          return acc;
        },
        [] as typeof orgFacilityList
      );

      setFilteredOrgFacilityList(filteredList);
      return;
    }

    const filteredList = orgFacilityList?.reduce(
      (acc, org) => {
        // Check if organization name matches
        const orgNameMatches = org.displayName.toLowerCase().includes(searchTerm);

        // Filter facilities that match the search term
        const matchingFacilities = (org.facilities || []).filter((facility) =>
          facility.displayName.toLowerCase().includes(searchTerm)
        );

        // Include org in results if either:
        // 1. Org name matches (include all facilities)
        // 2. Any facilities match (include only matching facilities)
        if (orgNameMatches || matchingFacilities.length > 0) {
          acc.push({
            ...org,
            facilities: orgNameMatches ? org.facilities : matchingFacilities,
          });
        }

        return acc;
      },
      [] as typeof orgFacilityList
    );

    setFilteredOrgFacilityList(filteredList);
  }, [facilitySearchQuery, orgFacilityList, offlineFacilitiesByOrg]);

  const sortedFilteredOrgFacilityList = useMemo(() => {
    return filteredOrgFacilityList
      ?.sort((a, b) => a.displayName.localeCompare(b.displayName))
      .reduce(
        (acc, org) => {
          acc.push({
            ...org,
            facilities: org.facilities?.sort((a, b) => a.displayName.localeCompare(b.displayName)),
          });

          return acc;
        },
        [] as typeof filteredOrgFacilityList
      );
  }, [filteredOrgFacilityList]);

  return {
    filteredOrgFacilityList: sortedFilteredOrgFacilityList,
  };
};
